import Button from '@carvana/ds-button';
import { icon } from '@carvana/ds-styles';
import { SearchSource } from '@carvana/merch-contracts';
import cx from 'classnames';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useContext } from 'react';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import SearchEventsDataContext from '~/context/srp/SearchEventsDataContext';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import getPaginationRange from '~/utilities/getPaginationRange';
import getTestIdToDisplay from './getTestIdToDisplay';
import { usePagination } from '../hooks';

const navButtonClasses =
  'bg-background-default shadow-[inset_0_0_0_1px_var(--border-subtle)] hover:bg-background-weak-hover disabled:bg-background-disabled-subtle disabled:shadow-none';

const MAX_PAGES = 25;

export default function Pagination() {
  const { setSearchSource } = useContext(SearchEventsDataContext);
  const { pagination: currentPagination, updatePagination } = usePagination();
  const { currentPage = 1, totalMatchedPages = 1 } = currentPagination;
  const trackEvent = useTrackEvent();
  const lastPage = Math.min(totalMatchedPages, MAX_PAGES);
  const isLastPage = currentPage === lastPage;
  const isFirstPage = currentPage === 1;
  const paginationNumbers = getPaginationRange(currentPage, lastPage);

  return (
    <nav aria-label="Pagination">
      <ul
        className="mx-auto flex w-fit list-none gap-sm pt-2xl sm:pt-4xl"
        data-testid="pagination-wrapper"
        id="pagination"
      >
        <li>
          <Button
            aria-label="Go to previous page"
            data-adobe-id="go-to-previous-page"
            data-analytics-id={null}
            onClick={() => {
              trackEvent('Pagination - clicked', {
                controlType: 'previousPage',
                page: currentPage - 1
              });
              updatePagination('subtract');
              setSearchSource(SearchSource.Pagination);
            }}
            data-testid="previous-page"
            icon
            size="xs"
            disabled={isFirstPage}
            className={navButtonClasses}
          >
            <ChevronLeft
              aria-hidden="true"
              size={16}
              color={isFirstPage ? icon.disabledSubtle : icon.strong}
            />
          </Button>
        </li>

        {paginationNumbers.map(number => {
          const isDottedNumber = number < 0;

          return (
            <li
              key={number}
              className={isDottedNumber ? 'self-end' : undefined}
            >
              {isDottedNumber ? (
                <span
                  aria-hidden="true"
                  className="flex min-w-32 items-center px-md t-body-sm text-text-strong"
                >
                  …
                </span>
              ) : (
                <Button
                  aria-current={number === currentPage ? 'page' : undefined}
                  data-adobe-id={`go-to-page-${number}`}
                  data-analytics-id={null}
                  data-testid={getTestIdToDisplay(
                    currentPage,
                    totalMatchedPages,
                    number
                  )}
                  className={cx(
                    'min-w-32 bg-background-default px-md',
                    number === currentPage
                      ? 'pointer-events-none'
                      : 'hover:bg-background-weak-hover',
                    number === currentPage
                      ? 'bg-background-primary text-text-inverse'
                      : 'text-text-strong',
                    number === currentPage
                      ? 'shadow-none'
                      : 'shadow-[inset_0_0_0_1px_var(--border-subtle)]'
                  )}
                  onClick={() => {
                    trackEvent('Pagination - clicked', {
                      controlType: 'pageNumber',
                      page: number
                    });
                    updatePagination('custom', number);
                    setSearchSource(SearchSource.Pagination);
                  }}
                  size="xs"
                >
                  {formatAndDropDecimal(number)}
                </Button>
              )}
            </li>
          );
        })}

        <li>
          <Button
            aria-label="Go to next page"
            data-adobe-id="go-to-next-page"
            data-analytics-id={null}
            data-testid="next-page"
            onClick={() => {
              trackEvent('Pagination - clicked', {
                controlType: 'nextPage',
                page: currentPage + 1
              });
              updatePagination('add');
              setSearchSource(SearchSource.Pagination);
            }}
            icon
            size="xs"
            disabled={isLastPage}
            className={navButtonClasses}
          >
            <ChevronRight
              aria-hidden="true"
              size={16}
              color={isLastPage ? icon.disabledSubtle : icon.strong}
            />
          </Button>
        </li>
      </ul>
    </nav>
  );
}
